/**
 * ListParts: the error / load-more pieces every keyset-paginated list needs.
 * @layer atom
 * @package @plerity/ui-core
 *
 * Extracted 2026-10-25 from the identical copies in `@plerity/audit-ui` and `@plerity/integrations-ui`
 * (backlog `duplication-2026-10-24-001`). Error text is injected through `describe`, so this stays free of any
 * feature package's error class. Error is never empty: a failed load renders `ErrorPanel`, a failed later page
 * renders `LoadMore`'s alert beside a retry button. Literal gray/red ramps with a `dark:` pair per color.
 */

import { Button } from "../Button/Button";
import { ErrorState } from "../ErrorState/ErrorState";
import type { ErrorPanelProps, InlineAlertProps, LoadMoreProps } from "./ListParts.types";

export function InlineAlert({ children }: InlineAlertProps) {
  return (
    <div
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-danger-strong dark:border-red-800 dark:bg-red-900/30"
    >
      {children}
    </div>
  );
}

/** A failed first load, with a retry. Never rendered as an empty list. */
export function ErrorPanel({ error, describe, onRetry, title, headingLevel = "h3" }: ErrorPanelProps) {
  return <ErrorState title={title} message={describe(error)} onRetry={onRetry} headingLevel={headingLevel} />;
}

/** "Load more" for a keyset list; a failed later page is reported beside the button and keeps the rows on screen. */
export function LoadMore({ hasMore, loading, error, describe, onLoadMore, noun, recoveryAction }: LoadMoreProps) {
  if (!hasMore && !error) return null;
  return (
    <div className="space-y-2">
      {error ? <InlineAlert>{describe(error)}</InlineAlert> : null}
      {recoveryAction ??
        (hasMore && (
          <Button type="button" variant="outline" loading={loading} onClick={onLoadMore}>
            {error ? `Try loading more ${noun}` : `Load more ${noun}`}
          </Button>
        ))}
    </div>
  );
}
